import type { Direction } from "@@/shared/directions";
import type { SectionDisplayType } from "@@/shared/sections/section-display-type";
import type { SectionItemDisplayType } from "@@/shared/sections/section-item-display-type";
import type { BackgroundStyle } from "@@/shared/stylable/background-style";
import { generateId } from "@@/utilities/generate-id";
import type { SizeName } from "../../stylable/sizes";
import type { SectionThemeItemSettings } from "./section-theme-item-settings";

const SECTION_THEME = "SECTION_THEME";
type SECTION_THEME = typeof SECTION_THEME;

type SectionThemeId = string;
type SectionTheme = {
    _id: SectionThemeId;
    _type: SECTION_THEME;
    name: string;
    version: number;
    fontFamily?: string;
    background?: BackgroundStyle;
    itemSettings?: SectionThemeItemSettings;
    displayType: SectionDisplayType;
    itemDisplayType: SectionItemDisplayType;
    wrap: boolean;
    direction: Direction;
    scroll: boolean;
    size: SizeName;
    asString: string;
    filters?: {
        bookables: boolean;
        buyables: boolean;
    };
    maxItems: number | undefined;
};

type CreateOptionals = Partial<
    Pick<
        SectionTheme,
        | "_id"
        | "displayType"
        | "itemDisplayType"
        | "direction"
        | "size"
        | "maxItems"
        | "scroll"
        | "version"
        | "wrap"
    >
>;

type CreateNotNeeded = Pick<SectionTheme, "asString" | "_type">;

const createSectionTheme = (
    params: Omit<
        Omit<SectionTheme, keyof CreateOptionals>,
        keyof CreateNotNeeded
    > &
        CreateOptionals,
): SectionTheme => {
    const {
        displayType: sectionDisplayType,
        itemDisplayType,
        direction,
        size,
        maxItems,
        _id,
    } = params;
    const asString = `${sectionDisplayType}-${itemDisplayType}-${direction}-${size}-MAX_${
        maxItems ?? "NO"
    }${params.scroll ? "-SCROLL" : ""}`;
    return {
        ...params,
        displayType: sectionDisplayType || "LIST",
        itemDisplayType: itemDisplayType || "ROW",
        direction: direction || "VT",
        size: size || "M",
        maxItems: maxItems || 500,
        wrap: params.wrap ?? false,
        scroll: !!params.scroll,
        version: params.version || 1,
        asString,
        _id: _id || generateId({ prefix: "section_theme__" }),
        _type: SECTION_THEME,
    };
};

const defaultSectionTheme = createSectionTheme({
    name: "DEFAULT",
    version: 1,
});

export { createSectionTheme, defaultSectionTheme, SECTION_THEME };
export type { SectionTheme, SectionThemeId };
